import styled from 'styled-components';
import { designTokens } from '../../../components/ui/foundation/DesignTokens';

// Media Grid Container
export const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${designTokens.spacing.lg};
  margin-top: ${designTokens.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: ${designTokens.spacing.md};
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${designTokens.spacing.sm};
  }
`;

// Media Card Component
export const MediaCard = styled.div`
  background: white;
  border-radius: ${designTokens.radius.lg};
  overflow: hidden;
  box-shadow: ${designTokens.shadows.sm};
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid ${designTokens.colors.gray[200]};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${designTokens.shadows.md};
    border-color: ${designTokens.colors.primary[200]};
  }

  &:active {
    transform: translateY(-2px);
  }
`;

// Media Card Thumbnail - with configurable background color
export const MediaCardThumbnail = styled.div`
  width: 100%;
  height: 140px;
  background: ${props => props.bgColor || designTokens.colors.gray[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 600px) {
    height: 120px;
  }
`;

// Media Card Icon (for folders, PDFs, videos without preview)
export const MediaCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 3rem;
    color: ${props => props.iconColor || designTokens.colors.gray[400]};
  }

  @media (max-width: 600px) {
    svg {
      font-size: 2.5rem;
    }
  }
`;

// Media Card Info
export const MediaCardInfo = styled.div`
  padding: ${designTokens.spacing.md};

  @media (max-width: 600px) {
    padding: ${designTokens.spacing.sm};
  }
`;

// Media Card Title
export const MediaCardTitle = styled.h4`
  font-family: ${designTokens.fonts.heading};
  font-weight: 600;
  font-size: 0.9rem;
  color: ${designTokens.colors.gray[900]};
  margin: 0 0 ${designTokens.spacing.xs} 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 0.85rem;
  }
`;

// Media Card Meta Row - for file count and date
export const MediaCardMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${designTokens.spacing.sm};
  margin-top: 4px;
`;

// Media Card Meta with optional icon
export const MediaCardMeta = styled.p`
  font-size: 0.8rem;
  color: ${designTokens.colors.gray[500]};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    font-size: 0.75rem;
    color: ${props => props.iconColor || designTokens.colors.gray[400]};
  }

  @media (max-width: 600px) {
    font-size: 0.75rem;
  }
`;

// Toolbar Container - no bottom border
export const MediaToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designTokens.spacing.lg};
  flex-wrap: wrap;
  gap: ${designTokens.spacing.lg};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${designTokens.spacing.md};
  }
`;

// Toolbar Left Section (Search + Upload Button)
export const MediaToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.md};
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${designTokens.spacing.sm};
  }
`;

// Toolbar Right Section (Order By)
export const MediaToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.md};
  flex-shrink: 0;
  justify-content: flex-end;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

// Order By Container
export const OrderByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};

  span {
    font-size: 0.875rem;
    color: ${designTokens.colors.gray[500]};
  }
`;

// Empty State
export const MediaEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${designTokens.spacing['3xl']};
  text-align: center;
  color: ${designTokens.colors.gray[500]};
`;

// Empty State Icon
export const MediaEmptyIcon = styled.div`
  margin-bottom: ${designTokens.spacing.lg};
  color: ${designTokens.colors.gray[300]};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 4rem;
    height: 4rem;
  }

  @media (max-width: 600px) {
    svg {
      width: 3rem;
      height: 3rem;
    }
  }
`;

// Empty State Text
export const MediaEmptyText = styled.p`
  font-size: 1rem;
  color: ${designTokens.colors.gray[500]};
  margin: 0;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

// Legacy Exports - Keeping for backwards compatibility during migration
export const MultimediaActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designTokens.spacing.lg};
  flex-wrap: wrap;
  gap: ${designTokens.spacing.md};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchContainer = styled.div`
  width: 18rem;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

export const MultimediaSortLabel = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  color: ${designTokens.colors.gray[500]};
  margin-right: ${designTokens.spacing.sm};
`;

export const MultimediaCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${designTokens.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: ${designTokens.spacing.md};
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${designTokens.spacing.sm};
  }
`;

export const MultimediaCard = styled.div`
  width: 100%;
`;

export const MultimediaEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${designTokens.spacing['3xl']};
  text-align: center;
  color: ${designTokens.colors.gray[500]};
  font-family: ${designTokens.fonts.body};
  font-size: 1rem;
`;
